(() => {
  'use strict';

  if (window.LdsPurchasePanel?.install) return;
  const instances = new WeakMap();

  const isElement = value => value && typeof value === 'object' && typeof value.querySelectorAll === 'function';

  const nodesFor = (value, scope, fallbackSelector) => {
    if (value == null) return fallbackSelector ? [...scope.querySelectorAll(fallbackSelector)] : [];
    if (typeof value === 'string') return [...scope.querySelectorAll(value)];
    if (Array.isArray(value) || (typeof value.length === 'number' && !isElement(value))) return [...value].filter(isElement);
    return isElement(value) ? [value] : [];
  };

  const parseObject = value => {
    if (!value) return {};
    if (typeof value === 'string') {
      try { return JSON.parse(value) || {}; } catch (_error) { return {}; }
    }
    return typeof value === 'object' ? value : {};
  };

  const numberFrom = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const integerAtLeast = (value, fallback) => Math.max(1, Math.floor(numberFrom(value, fallback)));

  const install = (root, options = {}) => {
    if (!isElement(root)) return null;
    const existing = instances.get(root);
    if (existing) return existing.api;

    const form = isElement(options.form)
      ? options.form
      : root.querySelector(options.form || 'form');
    if (!isElement(form)) return null;

    root.setAttribute('data-lds-purchase-panel', '');

    const visibleInputs = nodesFor(
      options.visibleInput,
      root,
      '[data-lds-visible-quantity]'
    );
    const hiddenInputs = nodesFor(
      options.hiddenInput,
      form,
      '[name="quantity"]'
    );
    const quantityButtons = nodesFor(options.quantityButtons, root, '[data-qty-change]');
    const addButtons = nodesFor(options.addButtons, root, '[data-lds-add]');
    const stickyPrices = nodesFor(options.stickyPrice, root, '[data-lds-sticky-price]');
    const stickyAdds = nodesFor(options.stickyAdd, root, '[data-lds-sticky-add]');
    const rules = parseObject(options.quantityRules || root.dataset.quantityRules || root.dataset.ldsQuantityRules);
    const firstVisible = visibleInputs[0] || null;
    const firstHidden = hiddenInputs[0] || null;
    const money = typeof options.formatMoney === 'function'
      ? options.formatMoney
      : cents => new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: root.dataset.currency || 'USD'
      }).format(numberFrom(cents, 0) / 100);

    let selectedVariant = null;
    let selectedValid = false;
    let currentRule = null;
    let lastValidQuantity = 1;

    const fallbackRule = () => {
      const source = firstVisible || firstHidden;
      const minimum = integerAtLeast(source?.getAttribute('min'), 1);
      const step = integerAtLeast(source?.getAttribute('step'), 1);
      const rawMaximum = source?.getAttribute('max');
      const maximum = rawMaximum == null || rawMaximum === '' ? Infinity : Math.max(minimum, integerAtLeast(rawMaximum, minimum));
      return { min: minimum, max: maximum, increment: step };
    };

    const ruleFor = variant => {
      const id = variant?.id == null ? '' : String(variant.id);
      const fromMap = rules[id] || rules.default || rules['*'];
      const fromVariant = variant?.quantity_rule || variant?.quantityRule;
      const source = fromMap || fromVariant || {};
      const fallback = fallbackRule();
      const min = integerAtLeast(source.min, fallback.min);
      const increment = integerAtLeast(source.increment ?? source.step, fallback.increment);
      const rawMax = source.max;
      const max = rawMax == null || rawMax === '' || rawMax === Infinity
        ? (Object.prototype.hasOwnProperty.call(source, 'max') ? Infinity : fallback.max)
        : Math.max(min, integerAtLeast(rawMax, min));
      return { min, max, increment };
    };

    const upperStepValue = rule => {
      if (!Number.isFinite(rule.max)) return Infinity;
      return rule.min + Math.max(0, Math.floor((rule.max - rule.min) / rule.increment)) * rule.increment;
    };

    const normalizeQuantity = (value, rule = currentRule || fallbackRule()) => {
      const raw = Number(value);
      if (!Number.isFinite(raw)) return rule.min;
      const floored = Math.floor(raw);
      const steps = Math.max(0, Math.floor((floored - rule.min) / rule.increment));
      const upper = upperStepValue(rule);
      return Math.max(rule.min, Math.min(rule.min + steps * rule.increment, upper));
    };

    const setRuleAttributes = rule => {
      [...visibleInputs, ...hiddenInputs].forEach(input => {
        input.setAttribute('min', String(rule.min));
        input.setAttribute('step', String(rule.increment));
        if (Number.isFinite(rule.max)) input.setAttribute('max', String(rule.max));
        else input.removeAttribute('max');
      });
    };

    const setVisible = value => visibleInputs.forEach(input => { input.value = String(value); });
    const setHidden = value => hiddenInputs.forEach(input => { input.value = String(value); });

    const renderedMoney = cents => {
      const result = money(cents);
      return result == null ? '' : String(result);
    };

    const renderTotals = () => {
      const unitPrice = numberFrom(selectedVariant?.price, NaN);
      const total = Number.isFinite(unitPrice) ? unitPrice * lastValidQuantity : NaN;
      const totalText = Number.isFinite(total) ? renderedMoney(total) : '';
      const unavailable = !selectedValid || !Number.isFinite(unitPrice);

      addButtons.forEach(button => {
        if (unavailable) button.disabled = true;
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
        const label = button.querySelector('.hdt-btn-atc_text') || button;
        if (!unavailable) label.textContent = `Add to cart · ${totalText}`;
      });
      stickyPrices.forEach(price => {
        price.textContent = unavailable ? 'Unavailable' : `Qty ${lastValidQuantity} · Total ${totalText}`;
      });
      stickyAdds.forEach(button => {
        if (unavailable) button.disabled = true;
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
        const label = button.querySelector('.hdt-btn-atc_text') || button;
        if (!unavailable) label.textContent = 'Add to cart';
      });
    };

    const commitQuantity = ({ preserveEmpty = false } = {}) => {
      const raw = firstVisible?.value;
      if (preserveEmpty && firstVisible && String(raw ?? '').trim() === '') {
        hiddenInputs.filter(input => !visibleInputs.includes(input)).forEach(input => { input.value = String(lastValidQuantity); });
        renderTotals();
        return lastValidQuantity;
      }
      const source = firstVisible && String(raw ?? '').trim() !== '' ? raw : firstHidden?.value;
      lastValidQuantity = normalizeQuantity(source == null || source === '' ? lastValidQuantity : source);
      setVisible(lastValidQuantity);
      setHidden(lastValidQuantity);
      renderTotals();
      return lastValidQuantity;
    };

    const onVisibleInput = event => {
      const input = event.currentTarget;
      const raw = input.value;
      // Keep intermediate digits (and an empty field) editable. Commit rules
      // on blur/change/add; valid input updates both totals immediately.
      visibleInputs.filter(other => other !== input).forEach(other => { other.value = raw; });
      const numeric = Number(raw);
      if (raw.trim() !== '' && Number.isFinite(numeric) && numeric === normalizeQuantity(numeric)) {
        lastValidQuantity = numeric;
      }
      hiddenInputs.filter(other => !visibleInputs.includes(other)).forEach(other => { other.value = String(lastValidQuantity); });
      renderTotals();
    };

    const onQuantityStep = event => {
      event.preventDefault();
      if (options.manageNativeQuantity) event.stopImmediatePropagation();
      const delta = numberFrom(event.currentTarget.dataset.qtyChange, 0);
      if (!delta) return;
      lastValidQuantity = normalizeQuantity(lastValidQuantity + delta * currentRule.increment);
      setVisible(lastValidQuantity);
      setHidden(lastValidQuantity);
      renderTotals();
      if (options.manageNativeQuantity) visibleInputs.forEach(input => input.dispatchEvent(new Event('change', {bubbles: true})));
    };

    const normalizeNow = () => commitQuantity();

    visibleInputs.forEach(input => {
      input.setAttribute('data-lds-purchase-quantity-input', '');
      input.addEventListener('input', onVisibleInput);
      input.addEventListener('blur', normalizeNow);
      input.addEventListener('change', normalizeNow, true);
      input.addEventListener('keydown', event => { if (event.key === 'Enter') normalizeNow(); }, true);
    });
    quantityButtons.forEach(button => button.addEventListener('click', onQuantityStep, Boolean(options.manageNativeQuantity)));

    // These capture handlers run before an existing submit/requestSubmit handler.
    [...addButtons, ...stickyAdds].forEach(button => button.addEventListener('click', normalizeNow, true));
    form.addEventListener('submit', normalizeNow, true);

    const render = (variant, valid) => {
      selectedVariant = variant || null;
      selectedValid = Boolean(valid);
      currentRule = ruleFor(selectedVariant);
      setRuleAttributes(currentRule);
      lastValidQuantity = normalizeQuantity(lastValidQuantity, currentRule);
      setVisible(lastValidQuantity);
      setHidden(lastValidQuantity);
      renderTotals();
    };

    currentRule = ruleFor(null);
    const initialValue = firstVisible?.value || firstHidden?.value || currentRule.min;
    lastValidQuantity = normalizeQuantity(initialValue, currentRule);
    setRuleAttributes(currentRule);
    setVisible(lastValidQuantity);
    setHidden(lastValidQuantity);

    const api = { render, normalizeNow };
    instances.set(root, { api });
    return api;
  };

  window.LdsPurchasePanel = window.LdsPurchasePanel || {};
  window.LdsPurchasePanel.install = install;
})();
